/**
 * 动态词汇的数据请求（TanStack Query），接口与旧版页面一致
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dynamicLexemeApi } from '@/api/dynamic-lexemes'
import { useAuthStore } from '@/stores/auth'
import type {
  DynamicLexemeSearchItem,
  DynamicLexemeStatus,
  ReviewRequest,
  SearchParams
} from '@/types/dynamic-lexemes'

const LEXEMES_KEY = ['admin', 'dynamic-lexemes'] as const

export function useLexemeStats() {
  return useQuery({
    queryKey: [...LEXEMES_KEY, 'stats'],
    queryFn: () => dynamicLexemeApi.getStats()
  })
}

// 待审核页取 50 条；已批准页传 approved 状态取 100 条（与旧版一致）
export function useLexemeReviewList(limit: number, statusFilter?: DynamicLexemeStatus) {
  return useQuery({
    queryKey: [...LEXEMES_KEY, 'review-list', limit, statusFilter],
    queryFn: () => dynamicLexemeApi.getPendingReview(limit, statusFilter)
  })
}

// params 为 null 表示还没有搜索（重置后不发请求）
export function useLexemeSearch(params: SearchParams | null) {
  return useQuery({
    queryKey: [...LEXEMES_KEY, 'search', params],
    enabled: params !== null,
    queryFn: async () => {
      const response = await dynamicLexemeApi.searchLexemes(params ?? {})
      return {
        items: response.data as unknown as DynamicLexemeSearchItem[],
        total: response.pagination?.total ?? 0
      }
    },
    placeholderData: keepPreviousData
  })
}

// 审核人取当前登录的管理员（旧版写死为 1）；成功后刷新列表和统计
export function useReviewLexemes() {
  const queryClient = useQueryClient()
  const reviewerId = useAuthStore((state) => state.user?.id)
  return useMutation({
    mutationFn: (request: ReviewRequest) => {
      if (reviewerId === undefined) throw new Error('未获取到当前管理员')
      return dynamicLexemeApi.reviewLexemes(reviewerId, request)
    },
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LEXEMES_KEY })
  })
}
